import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Clock, List, Grid, Phone, Mail, MapPin, Loader, Activity } from "react-feather";
import ReactImageFallback from "react-image-fallback";
import { getIngredientImagePlaceholder } from "../../../utils/ingredient";
import supplierService from "../../../services/supplierService";
import { IApplicationState } from "../../../redux/reducers";
import ActivityDrawer from "./ActivityDrawer";
import agentActivityService from "../../../services/agentActivityService";
import notification from "../../../utils/notification";
import './Supplier.css'
interface IProps extends RouteComponentProps {
	history: any;
	allSupplier: any[];
	isLoaded: boolean;
	getAllSupplierByUser: () => Promise<any>;
}
const Supplier: React.FC<IProps> = ({ history, getAllSupplierByUser, allSupplier, isLoaded }) => {
	const [viewMode, setViewMode] = useState("grid");
	const [isLoading, setIsLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
	const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false);
	const [selectedSupplier, setSelectedSupplier] = useState<any>(null);

	useEffect(() => {
		if (isLoaded) return;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				await getAllSupplierByUser();
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	}, [getAllSupplierByUser, isLoaded]);

	const renderStatusBadge = (status: string) => {
		const statusClass = `supplier-status-badge supplier-status-${status.toLowerCase()}`;
		return <span className={statusClass}>{status}</span>;
	};

	const formatDisplayDate = (date: any) => {
		if (!date) return "–";
		const parsedDate = new Date(date);
		return isNaN(parsedDate.getTime()) ? "–" : parsedDate.toDateString();
	};

	const handleConnectClick = (e: React.MouseEvent, supplier: any) => {
		e.stopPropagation();
		setSelectedSupplier(supplier);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setSelectedSupplier(null);
	};

	const openEmailModal = () => {
		setIsModalOpen(false);
		setIsEmailModalOpen(true);
	};

	const closeEmailModal = () => {
		setIsEmailModalOpen(false);
	};

	const handleActivityClick = (e: React.MouseEvent, supplier: any) => {
		e.stopPropagation();
		setSelectedSupplier(supplier);
		setIsActivityDrawerOpen(true);
	};

	const closeActivityDrawer = () => {
		setIsActivityDrawerOpen(false);
		setSelectedSupplier(null);
	};

	return (
		<div className="supplier-dashboard-wrapper">
			{/* Main Content */}
			<div className="supplier-main-content">
				{/* Header */}
				<div className="supplier-header">
					<h1>Supplier List</h1>
					<div className="supplier-view-toggle">
						<button
							onClick={() => setViewMode("grid")}
							className={viewMode === "grid" ? "active" : ""}
						>
							<Grid color={viewMode === "grid" ? "white" : "black"} size={18} />
						</button>
						<button
							onClick={() => setViewMode("list")}
							className={viewMode === "list" ? "active" : ""}
						>
							<List color={viewMode === "list" ? "white" : "black"} size={18} />
						</button>
					</div>
				</div>
				{/* Content */}
				{isLoading ? (
					<Loader className="fa-spin" style={{ margin: "auto" }} />
				) : (
					<div className={`supplier-list ${viewMode}`}>
						{viewMode === "grid" ? (
							<>
								{allSupplier?.map((supplier: any) => (
									<div
										key={supplier._id}
										className="supplier-card grid"
										onClick={() => history.push(`/app/supplier/${supplier._id}`)}
										style={{ cursor: "pointer" }}
									>
										<div className="supplier-card-header">
											<div className="supplier-info">
												<div className="avatar">
													<ReactImageFallback
														src={supplier.image_url || "/placeholder.svg"}
														fallbackImage={getIngredientImagePlaceholder()}
														alt={supplier.name}
														className="avatar-image"
													/>
												</div>
												<div>
													<h3>{supplier.name}</h3>
													<div className="supplier-location">
														<MapPin size={12} />
														{supplier.address || "Location not set"}
													</div>
												</div>
											</div>
											{renderStatusBadge(supplier.status || "Pending")}
										</div>
										<div className="supplier-card-content">
											<div className="supplier-score">
												Score: {supplier.mean_nutrition_score || 90}/100
											</div>
											<div className="supplier-updated">
												<Clock size={12} style={{ marginRight: 4 }} />
												Last updated: {supplier.lastUpdated || formatDisplayDate(supplier.updatedAt || supplier.createdAt)}
											</div>
											<div className="supplier-ingredients">
												<p>Ingredients:</p>
												<div className="supplier-tags mb-1">
													{(supplier.ingredients || []).slice(0, 3).map((ingredient: any, index: any) => (
														<span
															key={index}
															className="supplier-tag"
														>
															{ingredient.name || ingredient}
														</span>
													))}
													{supplier.ingredients?.length > 3 && (
														<span className="supplier-tag-more">
															+{supplier.ingredients.length - 3} more
														</span>
													)}
												</div>
											</div>
											<div className="supplier-footer">
												<span
													className="supplier-phone"
													onClick={(e) => {
														e.stopPropagation();
														if (supplier.phone_number) {
															const cleanNumber = supplier.phone_number.replace(/[^0-9]/g, '');
															window.open(`https://wa.me/${cleanNumber}`, '_blank');
														}
													}}
													style={{
														cursor: supplier.phone_number ? 'pointer' : 'default',
														position: 'relative'
													}}
													title={supplier.phone_number ? `Click to chat on WhatsApp: ${supplier.phone_number}` : ''}
												>
													<Phone size={12} />
													<span>{supplier.phone_number || "N/A"}</span>
													{supplier.phone_number && (
														<svg
															width="14"
															height="14"
															viewBox="0 0 24 24"
															fill="#25D366"
															className="whatsapp-icon"
														>
															<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
														</svg>
													)}
												</span>
												<div className="supplier-footer-actions">
													<button
														className="supplier-activity-btn"
														onClick={(e) => handleActivityClick(e, supplier)}
													>
														<Activity size={14} className="activity-icon-small" />
														Activity
													</button>
													<button
														className="supplier-connect-btn"
														onClick={(e) => handleConnectClick(e, supplier)}
													>
														Connect
													</button>
												</div>
											</div>
										</div>
									</div>
								))}

								{/* Email Outreach Tracking Card */}
								<div className="email-tracking-card">
									<div className="email-tracking-header">
										<Mail size={20} color="#2563eb" />
										<h3>Email Outreach Tracking</h3>
									</div>
									<div className="email-tracking-content">
										<div className="email-tracking-icon-large">
											<Mail size={48} color="#d1d5db" />
										</div>
										<p className="email-tracking-empty-state">No emails sent yet</p>
										<p className="email-tracking-description">
											Connect with suppliers to start outreach
										</p>
									</div>
								</div>
							</>
						) : (
							<div className="supplier-table-wrapper">
								<div className="supplier-table">
									<table>
										<thead>
											<tr>
												<th>Supplier</th>
												<th>Location</th>
												<th>Ingredients</th>
												<th>Rating</th>
												<th>Status</th>
												<th>Actions</th>
											</tr>
										</thead>
										<tbody>
											{allSupplier?.map((supplier: any) => (
												<tr
													key={supplier._id}
													onClick={() => history.push(`/app/supplier/${supplier._id}`)}
													style={{ cursor: "pointer" }}
												>
													<td>
														<div className="supplier-info">
															<div className="avatar">
																<ReactImageFallback
																	src={supplier.image_url || "/placeholder.svg"}
																	fallbackImage={getIngredientImagePlaceholder()}
																	alt={supplier.name}
																	className="avatar-image"
																/>
															</div>
															<div>
																<div className="supplier-font-medium">{supplier.name}</div>
																<div className="supplier-text-sm supplier-text-gray-500">
																	Last updated: {supplier.lastUpdated || formatDisplayDate(supplier.updatedAt || supplier.createdAt)}
																</div>
															</div>
														</div>
													</td>
													<td>
														<div className="supplier-location">
															<MapPin size={12} />
															{supplier.address || "Location not set"}
														</div>
													</td>
													<td>
														<div className="supplier-tags">
															{(supplier.ingredients || []).slice(0, 2).map((ingredient: any, index: any) => (
																<span
																	key={index}
																	className="supplier-tag"
																>
																	{ingredient.name || ingredient}
																</span>
															))}
															{supplier.ingredients?.length > 2 && (
																<span className="supplier-tag-more">
																	+{supplier.ingredients.length - 2} more
																</span>
															)}
														</div>
													</td>
													<td>
														<span className="supplier-font-medium">{supplier.score || 90}/100</span>
													</td>
													<td>{renderStatusBadge(supplier.status || "Pending")}</td>
													<td>
														<div className="supplier-actions">
															<button
																className="supplier-activity-btn"
																onClick={(e) => handleActivityClick(e, supplier)}
															>
																<Activity size={12} style={{ marginRight: 4 }} />
																Activity
															</button>
															<button
																className="supplier-connect-btn"
																onClick={(e) => handleConnectClick(e, supplier)}
															>
																<Mail size={12} style={{ marginRight: 4 }} />
																Connect
															</button>
															<button
																className="supplier-connect-btn"
																onClick={(e) => {
																	e.stopPropagation();
																	if (supplier.phone_number) {
																		window.location.href = `tel:${supplier.phone_number}`;
																	}
																}}
															>
																<Phone size={12} style={{ marginRight: 4 }} />
																Call
															</button>
															<button
																className="supplier-connect-btn"
																onClick={(e) => {
																	e.stopPropagation();
																	if (supplier.phone_number) {
																		const cleanNumber = supplier.phone_number.replace(/[^0-9]/g, '');
																		window.open(`https://wa.me/${cleanNumber}`, '_blank');
																	}
																}}
																style={{
																	backgroundColor: '#25D366',
																	borderColor: '#25D366'
																}}
																title={supplier.phone_number ? `Chat on WhatsApp: ${supplier.phone_number}` : 'No phone number available'}
															>
																<svg
																	width="12"
																	height="12"
																	viewBox="0 0 24 24"
																	fill="white"
																	style={{ marginRight: 4 }}
																>
																	<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
																</svg>
																WhatsApp
															</button>
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						)}
					</div>
				)}
			</div>

			{/* Supplier Detail Modal */}
			{isModalOpen && selectedSupplier && (
				<SupplierDetailModal
					isOpen={isModalOpen}
					supplier={selectedSupplier}
					onClose={closeModal}
					onOpenEmail={openEmailModal}
				/>
			)}

			{/* Email Composition Modal */}
			{isEmailModalOpen && selectedSupplier && (
				<EmailCompositionModal
					isOpen={isEmailModalOpen}
					supplier={selectedSupplier}
					onClose={closeEmailModal}
				/>
			)}

			{/* Agent Activity Drawer */}
			<ActivityDrawer
				isOpen={isActivityDrawerOpen}
				onClose={closeActivityDrawer}
				supplier={selectedSupplier}
			/>
		</div>
	);
};

// Supplier Detail Modal Component
const SupplierDetailModal: React.FC<{
	isOpen: boolean;
	supplier: any;
	onClose: () => void;
	onOpenEmail: () => void;
}> = ({
	isOpen,
	supplier,
	onClose,
	onOpenEmail
}) => {
		if (!isOpen) return null;

		return (
			<div className="supplier-modal-overlay" onClick={onClose}>
				<div className="supplier-modal-content" onClick={(e) => e.stopPropagation()}>
					<button className="supplier-modal-close" onClick={onClose}>
						×
					</button>

					<div className="supplier-modal-header">
						<div className="supplier-modal-avatar">
							<ReactImageFallback
								src={supplier.image_url || "/placeholder.svg"}
								fallbackImage={getIngredientImagePlaceholder()}
								alt={supplier.name}
								className="supplier-modal-avatar-image"
							/>
						</div>
						<div className="supplier-modal-title-section">
							<h2>{supplier.name}</h2>
							{renderStatusBadge(supplier.status || "Pending")}
						</div>
						<div className="supplier-modal-location">
							<MapPin size={14} />
							<span>{supplier.address || "Location not set"}</span>
						</div>
					</div>

					<div className="supplier-modal-body">
						<div className="supplier-modal-score-section">
							<div className="supplier-modal-score-item">
								<span className="supplier-modal-score-label">Quality Score</span>
								<h2 className="supplier-modal-score-value">{supplier.mean_nutrition_score || 72.73}/100</h2>
							</div>
							<div className="supplier-modal-score-item">
								<span className="supplier-modal-score-label">Last Updated</span>
								<p className="supplier-modal-score-date">
									{supplier.lastUpdated || (
										(supplier.updatedAt || supplier.createdAt)
											? new Date(supplier.updatedAt || supplier.createdAt).toDateString()
											: "–"
									)}
								</p>
							</div>
						</div>

						<div className="supplier-modal-section">
							<h3>About</h3>
							<p>{supplier.description || "A leading supplier of organic superfoods and functional ingredients."}</p>
						</div>

						<div className="supplier-modal-section">
							<h3>Ingredients Supplied</h3>
							<div className="supplier-modal-tags">
								{(supplier.ingredients || []).map((ingredient: any, index: number) => (
									<span key={index} className="supplier-modal-tag">
										{ingredient.name || ingredient}
									</span>
								))}
							</div>
						</div>

						<div className="supplier-modal-section">
							<h3>Certifications</h3>
							<div className="supplier-modal-certifications">
								<span className="supplier-modal-certification">✓ USDA Organic</span>
								<span className="supplier-modal-certification">✓ Non-GMO</span>
							</div>
						</div>

						<div className="supplier-modal-section">
							<h3>Contact Information</h3>
							<div className="supplier-modal-contact">
								<div className="supplier-modal-contact-item">
									<Mail size={16} />
									<span>{supplier.email || "contact@journeyfoods.com"}</span>
								</div>
								<div
									className="supplier-modal-contact-item"
									onClick={() => {
										if (supplier.phone_number) {
											const cleanNumber = supplier.phone_number.replace(/[^0-9]/g, '');
											window.open(`https://wa.me/${cleanNumber}`, '_blank');
										}
									}}
									style={{
										cursor: supplier.phone_number ? 'pointer' : 'default',
										transition: 'background-color 0.2s'
									}}
									onMouseEnter={(e) => {
										if (supplier.phone_number) {
											e.currentTarget.style.backgroundColor = '#f0f0f0';
										}
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.backgroundColor = 'transparent';
									}}
									title={supplier.phone_number ? 'Click to chat on WhatsApp' : ''}
								>
									<Phone size={16} />
									<span>{supplier.phone_number || "N/A"}</span>
									{supplier.phone_number && (
										<svg
											width="16"
											height="16"
											viewBox="0 0 24 24"
											fill="#25D366"
											style={{ marginLeft: 8 }}
										>
											<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
										</svg>
									)}
								</div>
							</div>
						</div>
					</div>

					<div className="supplier-modal-footer">
						<button className="supplier-modal-btn-secondary" onClick={onClose}>
							Close
						</button>
						<button className="supplier-modal-btn-primary" onClick={onOpenEmail}>
							<Mail size={16} style={{ marginRight: 8 }} />
							Send Outreach Email
						</button>
					</div>
				</div>
			</div>
		);
	};

// Email Composition Modal Component
const EmailCompositionModal: React.FC<{
	isOpen: boolean;
	supplier: any;
	onClose: () => void;
}> = ({ isOpen, supplier, onClose }) => {
	const [subject, setSubject] = useState("Partnership Inquiry - JourneyFoods");
	const [message, setMessage] = useState(
		`Hi ${supplier.name} Team,\n\nI hope this message finds you well. My name is Riana Lynn from JourneyFoods, and I'm reaching out because we're impressed with your ingredient offerings.\n\nWe're currently looking for suppliers who can provide high-quality ingredients for our food manufacturing partners. Based on your profile, we believe there could be a great opportunity for collaboration.\n\nWould you be interested in setting up a call to discuss potential partnership opportunities?\n\nAdditionally, we'd love to invite you to create a supplier profile on our platform, which will help streamline collaboration and help us match you with the right partners.`
	);
	const [isSending, setIsSending] = useState(false);

	if (!isOpen) return null;

	const handleSendEmail = async () => {
		if (!subject || !message) {
			notification.error("Subject and message are required.");
			return;
		}

		setIsSending(true);
		try {
			// 1. Initialize Agent Activity
			const initRes = await agentActivityService.initAgentActivity({
				supplier_name: supplier.name,
				supplier_email: supplier.email || "contact@journeyfoods.com"
			});

			const activity_id = initRes.data?.id || initRes.data?._id;

			if (!activity_id) {
				throw new Error("Failed to initialize agent activity.");
			}

			// 2. Send the actual email
			await agentActivityService.sendEmail({
				activity_id,
				supplier_email: supplier.email || "contact@journeyfoods.com",
				supplier_name: supplier.name,
				subject,
				body_html: `<p>${message.replace(/\n/g, "<br>")}</p>`
			});

			notification.success("Email sent successfully!");
			onClose();
		} catch (err) {
			const error = err as any;
			console.error("Email sending error:", error);
			notification.error(error.response?.data?.message || error.message || "Failed to send email. Please try again.");
		} finally {
			setIsSending(false);
		}
	};

	return (
		<div className="supplier-modal-overlay" onClick={onClose}>
			<div className="email-modal-content" onClick={(e) => e.stopPropagation()}>
				<button className="supplier-modal-close" onClick={onClose}>
					×
				</button>

				<div className="email-modal-header">
					<div className="email-modal-icon">
						<Mail size={24} color="#2563eb" />
					</div>
					<div>
						<h2>Send Outreach Email</h2>
						<p className="email-modal-recipient">
							To: {supplier.email || "contact@journeyfoods.com"}
						</p>
					</div>
				</div>

				<div className="email-modal-body">
					<div className="email-form-group">
						<label htmlFor="email-subject">Subject</label>
						<input
							id="email-subject"
							type="text"
							className="email-input"
							value={subject}
							onChange={(e) => setSubject(e.target.value)}
						/>
					</div>

					<div className="email-form-group">
						<label htmlFor="email-message">Message</label>
						<textarea
							id="email-message"
							className="email-textarea"
							rows={12}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
						/>
					</div>

					<div className="email-tracking-info">
						<div className="email-tracking-icon">
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
								<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
								<circle cx="12" cy="12" r="3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</div>
						<div>
							<strong>Email Tracking Enabled</strong>
							<p>You'll be notified when the recipient opens this email. The email includes an invitation for the supplier to sign up for a profile on JourneyFoods.</p>
						</div>
					</div>
				</div>

				<div className="email-modal-footer">
					<button className="supplier-modal-btn-secondary" onClick={onClose}>
						Cancel
					</button>
					<button
						className="supplier-modal-btn-primary"
						onClick={handleSendEmail}
						disabled={isSending}
					>
						<Mail size={16} style={{ marginRight: 8 }} />
						{isSending ? "Sending..." : "Send Email"}
					</button>
				</div>
			</div>
		</div>
	);
};

const renderStatusBadge = (status: string) => {
	const statusClass = `supplier-status-badge supplier-status-${status.toLowerCase()}`;
	return <span className={statusClass}>{status}</span>;
};
const mapStateToProps = (state: IApplicationState) => ({
	allSupplier: state.supplier.result,
	isLoaded: state.supplier.isLoaded
});
const mapDispatchToProps = {
	getAllSupplierByUser: () => supplierService.getAllSupplierByUser()
};
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Supplier));